import React, { ChangeEvent, useState } from 'react'

import { MdmDevice, useDeleteDeviceMutation, useGetObjectsByProjectQuery } from 'gql/generated/types';
import { Chips, ChipsChangeParams } from 'primereact/chips';
import { useNavigate, useOutletContext } from 'react-router-dom';
import InputLabelWrapper from 'components/basic/input-label-wrapper/InputLabelWrapper';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { easasAlert } from 'components/fragments/easas-global-fragment/EasasGlobalAlerts';
import { Dropdown } from 'primereact/dropdown';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getFormErrorMessage } from '../utils/utils';

type DeviceFormProps = {
  device: MdmDevice | null;
  onSubmit: (device: MdmDevice) => void;
  newForm?: boolean;
}

const defaultDevice: Partial<MdmDevice> = {
  tags: [],
}

const schema = z.object({
  identifier: z.string().min(1, "Required"),
  mdmObject: z.object({
    title: z.string(),
    id: z.string(),
  }),
})

const DeviceForm: React.FC<DeviceFormProps> = ({device, onSubmit, newForm = false}) => {
  const [editable, setEditable] = useState(newForm);
  const [deviceState, setDeviceState] = useState<MdmDevice>(device ?? defaultDevice as MdmDevice);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  });

  const projectId = useOutletContext<string>();
  const { data: objectData } = useGetObjectsByProjectQuery({
    variables: { id: projectId },
    skip: !projectId,
  });
  const [mutateFunction] = useDeleteDeviceMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Device deleted successfully",
        life: 3000,
      });
      navigate(-1);
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetAllDevicesByProject", "GetObject"],
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement> | ChipsChangeParams) => {
    const { name, value } = e.target;
    setDeviceState(prev => ({ ...prev, [name]: value }));
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    confirmPopup({
      target: e.currentTarget,
      message: "Are you sure you want to delete this device?",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => {
        mutateFunction({
          variables: {
            id: deviceState.id!,
          },
        });
      },
    })
  };

  return (
    <div className="mx-auto" style={{ maxWidth: "800px" }}>
      <ConfirmPopup />
      <form
        onSubmit={handleSubmit(() => onSubmit(deviceState))}
        className="flex justify-content-between flex-column gap-5"
      >
        <div className="grid p-fluid">
          <Controller
            defaultValue={deviceState.identifier ?? ""}
            control={control}
            name="identifier"
            render={({ field }) => (
              <InputLabelWrapper labelName="Identifier" col={4}>
                <InputText
                  value={field.value}
                  onChange={e => {
                    field.onChange(e.target.value);
                    handleChange(e);
                  }}
                  className="p-inputtext-sm"
                  name="identifier"
                  placeholder="Identifier"
                  disabled={!editable}
                />
                {getFormErrorMessage("identifier", errors)}
              </InputLabelWrapper>
            )}
          />
          <Controller
            defaultValue={{ title: deviceState.mdmObject?.title, id: deviceState.mdmObject?.id }}
            control={control}
            name="mdmObject"
            render={({ field }) => (
              <InputLabelWrapper labelName="Object" col={4}>
                <Dropdown
                  value={field.value}
                  options={objectData?.mdmObjectsByProjectId.map(object => ({
                    title: object.title,
                    id: object.id,
                  }))}
                  onChange={e => {
                    field.onChange(e.value);
                    setDeviceState(prev => ({ ...prev, mdmObject: e.value }));
                  }}
                  optionLabel="title"
                  name="mdmObject"
                  placeholder="Object"
                  className="p-inputtext-sm"
                  disabled={!editable}
                />
                {/* @ts-ignore} */}
                {errors.mdmObject?.title && <small className="p-error">{errors.mdmObject?.title.message as string}</small>}
              </InputLabelWrapper>
            )}
          />
          <div className="text-right col-4 mt-2 ">
            {!editable && (
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text"
                onClick={() => setEditable(true)}
              />
            )}
            {editable && !newForm && (
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-text p-button-danger"
                onClick={handleDelete}
              />
            )}
          </div>
          <InputLabelWrapper labelName="Tags" col={12}>
            <Chips
              value={deviceState.tags ?? []}
              onChange={handleChange}
              className="p-inputtext-sm"
              name="tags"
              placeholder="Tags.."
              disabled={!editable}
            />
          </InputLabelWrapper>
          {!newForm && (
            <>
              <InputLabelWrapper labelName="Created at" col={4}>
                <InputText
                  value={new Date(deviceState.createdAt).toUTCString() ?? ""}
                  className="p-inputtext-sm"
                  name="createdAt"
                  disabled
                />
              </InputLabelWrapper>
              <InputLabelWrapper labelName="Updated at" col={4}>
                <InputText
                  value={new Date(deviceState.modifiedAt).toUTCString() ?? ""}
                  className="p-inputtext-sm"
                  name="modifiedAt"
                  disabled
                />
              </InputLabelWrapper>
            </>
          )}
        </div>
        <div className="flex w-full justify-content-end gap-3">
          {editable && (
            <Button
              type="submit"
              label={newForm ? "Create" : "Save"}
              className={`p-button-rounded p-button-${newForm ? "success" : "primary"} w-10rem`}
            />
          )}
          {device && !newForm && editable && (
            <Button
              onClick={() => {
                setEditable(false);
                setDeviceState(device ?? {});
              }}
              label="Cancel"
              className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
            />
          )}
          {((newForm && !device) || !editable) && (
            <Button
              onClick={() => navigate(-1)}
              label="Back"
              className="p-button-rounded p-button-secondary p-button-outlined w-10rem"
            />
          )}
        </div>
      </form>
    </div>
  );
}

export default DeviceForm